import { create } from 'zustand';

export type ThemeMode = 'light' | 'dark' | 'system';

const prefersDark = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

/** Toggle the `dark` class on <html> based on the chosen mode. */
const apply = (mode: ThemeMode) => {
  const dark = mode === 'dark' || (mode === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', dark);
};

const stored = (): ThemeMode => {
  const t = (typeof localStorage !== 'undefined' && localStorage.theme) as ThemeMode;
  return t === 'light' || t === 'dark' || t === 'system' ? t : 'system';
};

interface ThemeState {
  mode: ThemeMode;
  /** true when the resolved theme is dark right now */
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  /** cycle light -> dark -> system */
  cycle: () => void;
}

export const useTheme = create<ThemeState>((set, get) => ({
  mode: stored(),
  isDark: stored() === 'dark' || (stored() === 'system' && prefersDark()),
  setMode: (mode) => {
    if (typeof localStorage !== 'undefined') localStorage.theme = mode;
    apply(mode);
    set({ mode, isDark: mode === 'dark' || (mode === 'system' && prefersDark()) });
  },
  cycle: () => {
    const order: ThemeMode[] = ['light', 'dark', 'system'];
    const next = order[(order.indexOf(get().mode) + 1) % order.length];
    get().setMode(next);
  },
}));

// Keep 'system' mode reactive to OS changes.
if (typeof window !== 'undefined') {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    const { mode, setMode } = useTheme.getState();
    if (mode === 'system') setMode('system');
  });
}
